import React, {useState, useMemo, useContext} from 'react'
import Box from "@mui/material/Box"
import {Button} from "@mui/material"
import Typography from "@mui/material/Typography"
import {TranslationContext} from "@/configs/context"
import { Carousel } from 'react-responsive-3d-carousel'
import { motion } from 'framer-motion'

import {alpha, useTheme} from "@mui/material/styles"
import useMediaQuery from "@mui/material/useMediaQuery"
import {GLOBAL_PATHS} from "@/lib/globals"

import 'react-responsive-3d-carousel/dist/styles.css'
import styles from './styles.module.css'
import { ToolPadGlassBox } from '../custom/box'


// Hook pour détecter mobile (remplace useMediaQuery)
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false)

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  return isMobile
}

export default function VCardCarrousel() {
  const theme = useTheme()
  const isMobile = useIsMobile()
  // @ts-ignore
  const { t }: object = useContext(TranslationContext)
  const matchMd = useMediaQuery(theme.breakpoints.up('md'))
  const matchXl = useMediaQuery(theme.breakpoints.up('xl'))

  const [curIndex, setCurIndex] = useState(0)
  const [isTextVisible, setIsTextVisible] = useState(true)

  // Animation de lévitation avec Framer Motion
  const levitationAnimation = {
    y: [0, -8, 0],
    rotate: [0, 1, -1, 0],
    scale: [1, 1.02, 1],
    transition: {
      duration: 4,
      ease: "easeInOut",
      repeat: Infinity,
      repeatType: "loop"
    }
  }

  // Animation fade pour le texte
  const textFadeAnimation = {
    opacity: isTextVisible ? 1 : 0,
    y: isTextVisible ? 0 : 10,
    transition: {
      duration: 1,
      ease: "easeInOut"
    }
  }

  // Données des VCards adaptées du composant VCardCarrousel
  const vcards = useMemo(() => ([
    {
      id: 'vcard-1',
      title: t('translation:hero.title1_1'),
      description: t('translation:hero.subtitle'),
      backgroundImage: `${GLOBAL_PATHS.COVERS}vcard4.webp`,
      coverImage: `${GLOBAL_PATHS.COVERS}bg.webp`,
    },
    {
      id: 'vcard-2',
      title: t('translation:hero.title1_2'),
      description: t('translation:hero.subtitle'),
      backgroundImage: `${GLOBAL_PATHS.COVERS}vcard-v1.webp`,
      coverImage: `${GLOBAL_PATHS.COVERS}bg-2.jpg`,
    },
    {
      id: 'vcard-3',
      title: t('translation:hero.title1_3'),
      description: t('translation:hero.subtitle'),
      backgroundImage: `${GLOBAL_PATHS.COVERS}vcard-std.webp`,
      coverImage: `${GLOBAL_PATHS.COVERS}bg-2.jpg`,
    },
    {
      id: 'vcard-4',
      title: t('translation:hero.title1_4'),
      description: t('translation:hero.subtitle'),
      backgroundImage: `${GLOBAL_PATHS.COVERS}vcard-douanes-cm.webp`,
      coverImage: `${GLOBAL_PATHS.COVERS}bgg.jpg`,
    },
    {
      id: 'vcard-5',
      title: t('translation:hero.title1_1'),
      description: t('translation:hero.subtitle'),
      backgroundImage: `${GLOBAL_PATHS.COVERS}vcard-v2.webp`,
      coverImage: `${GLOBAL_PATHS.COVERS}bgg.jpg`,
    },
  ]), [t])

  const layout = useMemo(() => {
    const layoutStyles = {
      "default": {
        "translate": {
          "x": 0,
          "y": 0,
          "z": 0,
        },
        "rotate": {
          "x": 0,
          "y": 0,
          "z": 0
        },
        "offset": {
          "x": 0,
          "y": 0,
          "z": 0
        }
      },
      "0": {
        width: '16rem',
        height: '25rem',
        // width: 'clamp(30%, 16rem, 25rem)',
        "translate": {
          "x": matchXl ? 0.45 : 0.5,
          "y": -0.1,
          "z": 0,
        },
        "rotate": {
          "x": 0,
          "y": 0,
          "z": 0
        },
        "offset": {
          "x": -1,
          "y": 0,
          "z": 0
        }
      }
    }

    for (let i = 1; i < vcards.length; i++) {
      layoutStyles[i] = {
        "height": "18rem",
        "width": "clamp(14rem, 15%, 8rem)",
        "translate": {
          "x": Math.cos(2 * Math.PI * i / vcards.length) * 0.15 - 0.2,
          "y": 0.1,
          "z": Math.sin(2 * Math.PI * i / vcards.length) * 0.15 - 0.3,
        },
        "rotate": {
          "x": 0,
          "y": - 360 * i / vcards.length,
          "z": 0,
        },
        "offset": {
          "x": 0,
          "y": 0,
          "z": 0,
        }
      }
    }

    return layoutStyles
  }, [vcards])

  const mobileLayout = useMemo(() => {
    const mobLayout = {
      "default": {
        "translate": {
          "x": 0,
          "y": 0,
          "z": 0,
        },
        "rotate": {
          "x": 0,
          "y": 0,
          "z": 0
        },
        "offset": {
          "x": 0,
          "y": 0,
          "z": 0
        }
      },
      "0": {
        width: '14rem',
        height: '20rem',
        // width: 'clamp(16rem, 90%, 25rem)',
        "translate": {
          "x": 0,
          "y": -0.25,
          "z": 0,
        },
        "rotate": {
          "x": 0,
          "y": 0,
          "z": 0
        },
        "offset": {
          "x": 0,
          "y": 0,
          "z": 0
        }
      }
    }

    for (let i = 1; i < vcards.length; i++) {
      mobLayout[i] = {
        "width": "clamp(5rem, 10%, 6rem)",
        "translate": {
          "x": Math.cos(2 * Math.PI * i / vcards.length) * 0.15,
          "y": 0.12,
          // "y": 0.28,
          "z": Math.sin(2 * Math.PI * i / vcards.length) * 0.15 - 0.1,
        },
        "rotate": {
          "x": 0,
          "y": - 360* i / vcards.length,
          "z": 0,
        },
        "offset": {
          "x": 0.15,
          // "x": -0.25,
          "y": 0,
          "z": 0,
        }
      }
    }

    return mobLayout
  }, [vcards])

// Composant Item pour chaque VCard
  function Item({ vcard }) {
    const theme = useTheme()

    return (
      <article className={styles['item']}>
        {!isMobile &&
          // eslint-disable-next-line @next/next/no-img-element
          <img
            alt={vcard.title}
            src={vcard.backgroundImage}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              border: "2px solid rgba(255, 255, 255, 0.75)",
              borderRadius: theme.spacing(2),
            }}
          />}
        {/*eslint-disable-next-line @next/next/no-img-element*/}
        <img
          alt=""
          src={vcard.backgroundImage}
          className={styles['reflection']}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            border: "2px solid rgba(255, 255, 255, 0.75)",
            borderRadius: theme.spacing(2),
          }}
        />
      </article>
    )
  }

  const handleChange = (currentIndex) => {
    // Fade out le texte avant de changer l'index
    setIsTextVisible(false)

    // Après un petit délai, change l'index et fade in le nouveau texte
    setTimeout(() => {
      setCurIndex(currentIndex)
      setIsTextVisible(true)
    }, 300)
  }

  const items = vcards.map((vcard) => {
    return <Item key={vcard.id} vcard={vcard} />
  })

  return (
    <Box className={styles['wrapper']}>
      <Carousel
        width="30%"
        items={items}
        height="auto"
        startIndex={0}
        aspectRatio={1}
        boxShadow='none'
        autoPlay={true}
        interval={5000}
        showArrows={false}
        showStatus={false}
        infiniteLoop={true}
        showIndicators={false}
        containerHeight='100%'
        onChange={handleChange}
        layout={isMobile ? mobileLayout : layout}
        perspectiveOrigin={isMobile ? 'center' : 'left'}
      />

      <Box className={styles['background']}>
        {/*eslint-disable-next-line @next/next/no-img-element*/}
        <img
          alt="cover"
          loading="eager"
          fetchPriority="high"
          src={vcards[curIndex].coverImage}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </Box>

      {isMobile &&
        <motion.img
          id="mobile-hero-img"
          alt={vcards[curIndex].title}
          src={vcards[curIndex].backgroundImage}
          animate={levitationAnimation}
          style={{
            top: "10%",
            left: "12.5%",
            position: "absolute",
            width: '75%',
            height: '68%',
            border: "2px solid rgba(255, 255, 255, 0.75)",
            borderRadius: theme.spacing(2),
          }}
        />}

      <ToolPadGlassBox sx={{
        bottom: '3%',
        right: isMobile ? 'auto' : '5%',
        width: isMobile ? 'auto' : '35%',
        padding: isMobile ? 2 : 2,
        mx: isMobile ? 2 : 'inherit',
        borderRadius: 4,
        lineHeight: 1,
        display: 'flex',
        whiteSpace: 'wrap',
        position: 'absolute',
        alignItems: 'center',
        pointerEvents: 'none',
        flexDirection: 'column',
        background: `${alpha(theme.palette.background.paper, .035)} !important`
      }}>
        <motion.div
          animate={textFadeAnimation}
          style={{ display: 'contents' }}
        >
          <Typography
            component="h1"
            variant={matchMd ? "h5" : "h5"}
            align="center" sx={{ mb: 1 }}
          >
            {vcards[curIndex].title}
          </Typography>

          <Typography component="h2" variant="body2">
            {vcards[curIndex].description}
          </Typography>
        </motion.div>

        {/*isMobile && 
        <Button
          variant="black-glass"
          href="#contact-section"
          // className="FluidScroll-anchor"
          sx={{
            zIndex: 999,
            borderRadius: 50,
            mt: 3, py: 1, px: 5,
            userSelect: "inherit !important"
          }}
        >
          {t('translation:hero.order')}
        </Button>*/}
      </ToolPadGlassBox>
    </Box>
  )
}